import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "../../api";

/* Módulo independiente de pedidos/pagos: retiros de saldo del revendedor.
 * Polling propio (8s) solo para detectar cambios de estado y avisar con
 * toast — no comparte estado ni intervalos con useOrderPolling. */
const STATUS_MESSAGES = {
  "En Proceso": { title: "Retiro en proceso", tone: "success",
    body: (w) => `Tu retiro ${w.code} está siendo procesado por el equipo AG7.` },
  Realizado: { title: "Retiro realizado", tone: "success",
    body: (w) => `Tu retiro ${w.code} por Bs ${Number(w.netBs).toFixed(2)} fue completado.` },
  Rechazado: { title: "Retiro rechazado", tone: "error",
    body: (w) => `Tu retiro ${w.code} fue rechazado. El saldo volvió a tu cuenta.` },
};

export function useWithdrawals({ user, showToast }) {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastStatusRef = useRef({});
  const seededRef = useRef(false);
  const isReseller = user?.role === "reseller";

  const load = useCallback(async () => {
    if (!isReseller) return null;
    try {
      const rows = await api.listMyWithdrawals();
      setWithdrawals(rows);
      return rows;
    } catch (e) {
      if (e.status !== 401) console.error("listMyWithdrawals failed:", e);
      return null;
    }
  }, [isReseller]);

  useEffect(() => {
    lastStatusRef.current = {};
    seededRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (!isReseller) return;
    setLoading(true);
    load().then((rows) => {
      if (rows) {
        rows.forEach((w) => { lastStatusRef.current[w.id] = w.status; });
        seededRef.current = true;
      }
    }).finally(() => setLoading(false));
  }, [isReseller, load]);

  useEffect(() => {
    if (!isReseller) return;
    const interval = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      const rows = await load();
      if (!rows || !seededRef.current) return;
      for (const w of rows) {
        const prev = lastStatusRef.current[w.id];
        if (prev && prev !== w.status && STATUS_MESSAGES[w.status]) {
          const cfg = STATUS_MESSAGES[w.status];
          showToast?.(cfg.title, cfg.body(w), cfg.tone);
        }
        lastStatusRef.current[w.id] = w.status;
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [isReseller, load, showToast]);

  return { withdrawals, loading, reload: load };
}
